import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import GanymedeInput from './GanymedeInput';
import { WALLETS_NEVER } from '../../lib/stripe-wallets';
import defaultCardIcon from '../../assets/svgs/credit-card-icon.svg';
import visa from '../../assets/svgs/visa.svg';
import amex from '../../assets/svgs/amex.svg';
import dinersClub from '../../assets/svgs/diners-club.svg';
import jcb from '../../assets/svgs/jcb.svg';
import discover from '../../assets/svgs/discover.svg';
import mastercard from '../../assets/svgs/mastercard.svg';
import { stripePromise } from '../../lib/stripe';
import api from '../../services/jupiter_api';

import Cvv from '../../assets/pngs/cvv-tip.png';

const ccType = require('credit-card-type');
const DEFAULT_CARD_DISPLAY_LENGTH = 4;

const cardIconMap = {
  visa,
  ['american express']: amex,
  jcb,
  discover,
  mastercard,
  ['diners-club']: dinersClub,
  undefined: defaultCardIcon,
};

/**
 * Inner component for the Stripe Elements "add card" form.
 * Must be rendered inside an <Elements> provider.
 */
function StripeCardForm({ paymentMethods, cancel, wallets }) {
  const stripe = useStripe();
  const elements = useElements();
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext();

  const [saving, setSaving] = useState(false);
  const [cardError, setCardError] = useState(null);
  const [cardSaved, setCardSaved] = useState(false);

  const zip = register('paymentMethod.postal_code', {
    required: { value: true, message: 'Zip code is required' },
  });

  // register hidden field so parent form can read it; required until card is confirmed
  register('paymentMethod.payment_method_id', {
    required: { value: true, message: 'Please save your card before continuing' },
  });

  const handleSaveCard = async () => {
    if (!stripe || !elements) return;
    setSaving(true);
    setCardError(null);

    let result;
    try {
      result = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: window.location.href,
        },
      });
    } catch (err) {
      setCardError(err.message || 'Payment verification failed. Please try again.');
      setSaving(false);
      return;
    }

    if (result.error) {
      setCardError(result.error.message);
      setSaving(false);
      return;
    }

    if (result.setupIntent?.payment_method) {
      setValue('paymentMethod.payment_method_id', result.setupIntent.payment_method, { shouldValidate: true });
      setCardSaved(true);
    }
    setSaving(false);
  };

  return (
    <div className={`add-cc-form grid grid-cols-6 gap-x-2.5`}>
      <GanymedeInput
        className={'col-span-full sm:col-span-2'}
        label='First Name'
        type='text'
        errors={errors}
        {...register('paymentMethod.first_name', {
          shouldUnregister: false,
          required: { value: true, message: 'First name is required' },
        })}
      />
      <GanymedeInput
        className={'col-span-full sm:col-span-2'}
        label='Last Name'
        type='text'
        errors={errors}
        {...register('paymentMethod.last_name', {
          shouldUnregister: false,
          required: { value: true, message: 'Last name is required' },
        })}
      />
      <GanymedeInput
        className={'col-span-full sm:col-span-2'}
        label='Zip'
        type='text'
        errors={errors}
        {...zip}
      />
      <div className={'col-span-full'}>
        {!cardSaved ? (
          <>
            <div style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
              <PaymentElement options={{ layout: 'tabs', wallets }} />
            </div>
            {cardError && (
              <div className={'form-validation-error'}>{cardError}</div>
            )}
            <button
              type='button'
              className={'cc-cancel-btn'}
              style={{
                backgroundColor: '#f97316',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1.5rem',
                borderRadius: '0.25rem',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
                marginTop: '0.5rem',
              }}
              onClick={handleSaveCard}
              disabled={saving || !stripe || !elements}
            >
              {saving ? 'Saving...' : 'Save Card'}
            </button>
          </>
        ) : (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem',
            backgroundColor: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '0.375rem',
            marginTop: '0.5rem',
          }}>
            <span style={{ color: '#16a34a', fontSize: '1.25rem' }}>&#10003;</span>
            <span style={{ color: '#15803d' }}>Card saved successfully</span>
          </div>
        )}
      </div>
      {paymentMethods?.length > 0 && (
        <div className={'cc-btn-group col-span-full'}>
          <button
            className={'cc-cancel-btn'}
            onClick={cancel}
            type='button'
          >
            Use Existing Card
          </button>
        </div>
      )}
    </div>
  );
}

function GanymedePaymentCardDisplay({
  paymentMethods = [],
  mode = 'select',
  setMode = () => {},
  selectedPaymentMethod,
  setPaymentMethod = () => {},
  clientId,
  wallets = WALLETS_NEVER, // opt-in per caller; see lib/stripe-wallets.js
  ...props
}) {
  const [showAllCards, setShowAllCards] = useState(false);
  const [setupIntentSecret, setSetupIntentSecret] = useState(null);
  const [setupIntentError, setSetupIntentError] = useState(null);
  const {
    register,
    unregister,
    setValue,
    formState: { errors },
    watch,
  } = useFormContext();
  const paymentMethod = watch('paymentMethod');
  const paymentMethodRadio = watch('paymentMethodRadio');

  register('paymentMethodRadio');

  const cvc = watch('paymentMethod.cvc');

  useEffect(() => {
    if (mode === 'select' && !paymentMethods.length) {
      setMode('add');
    }
    // eslint-disable-next-line
  }, [paymentMethods, mode]);

  // fetch SetupIntent when entering add mode
  useEffect(() => {
    if (mode === 'add') {
      setValue('paymentMethodRadio', undefined, { shouldValidate: true });
      setValue('paymentMethod', null);
      setPaymentMethod(null);
      setSetupIntentSecret(null);
      setSetupIntentError(null);

      (async () => {
        try {
          const { client_secret } = await api.createSetupIntent();
          setSetupIntentSecret(client_secret);
        } catch (e) {
          setSetupIntentError(e.message || 'Failed to initialize card form. Please try again.');
        }
      })();
    }
  }, [mode]);

  useEffect(() => {
    if (paymentMethodRadio) {
      setValue('paymentMethod', paymentMethods[paymentMethodRadio]);
      setValue('paymentMethod.cvc', cvc, { shouldValidate: true });
    }
  }, [paymentMethodRadio]);

  const cancel = () => {
    setMode('select');
    unregister('paymentMethod');
    setSetupIntentSecret(null);
  };

  const CardDisplay = ({ card }) => {
    const brand =
      card.brand?.toLowerCase() ||
      ccType(card.cardNumber || card.last4)[0]?.type;
    return (
      <div className={'flex w-full justify-between'}>
        <span className={'inline-card-display'}>
          <img
            className={'cc-icon'}
            src={cardIconMap[brand]}
            alt={'credit card icon'}
          />
          <span className={'CardNumber ml-2 text-lg sm:ml-4 sm:text-xl'}>
            <span className={'hidden sm:inline-block'}>
              ••••&nbsp;••••&nbsp;
            </span>
            {`•••• ${card?.cardNumber?.slice(-4) || card.last4}`}
          </span>
        </span>
        <span
          className={
            'absolute right-1 top-0.5 left-auto text-xs text-gray-400 sm:relative sm:right-auto sm:top-auto sm:text-base'
          }
        >
          {`expires ${card.exp_month}/${card.exp_year}`}
        </span>
      </div>
    );
  };

  return (
    <div className={`ganymede-payment-card-container ${props.className || ''}`}>
      <div className={'inner-ganymede-payment-card-container'}>
        <h4 style={{ marginBottom: mode === 'add' ? '0.63rem' : 0 }}>
          {mode === 'add' ? 'Add Payment Method' : 'Select Payment Method'}
        </h4>
        {/* display/select mode */}
        {mode === 'select' && paymentMethods?.length && (
          <>
            <div className={'change-btn-container'}>
              <div>
                <button
                  className={'ChangeButton'}
                  onClick={() => setMode('add')}
                >
                  Add New Card
                </button>
              </div>
            </div>
            <div className={'payment-methods'}>
              {paymentMethods
                ?.slice(0, DEFAULT_CARD_DISPLAY_LENGTH)
                ?.map((card, index) => {
                  return (
                    <label
                      className={`payment-method relative ${
                        paymentMethod?.external_id === card.external_id
                          ? 'selected-payment-method'
                          : ''
                      }`}
                      key={index}
                    >
                      <div>
                        <div className={'grid-span-3'}>
                          <input
                            name='paymentMethod'
                            type='radio'
                            {...register('paymentMethodRadio', {
                              value: index.toString(),
                              shouldUnregister: true,
                              required: {
                                value: true,
                                message: 'Select a card',
                              },
                            })}
                            value={index.toString()}
                            defaultChecked={
                              index.toString() === paymentMethodRadio
                            }
                          />
                        </div>
                        <CardDisplay card={card} />
                      </div>
                      {paymentMethod?.external_id === card.external_id && (
                        <div
                          className={
                            'cvv-capture grid grid-cols-6 grid-cols-6 gap-2 sm:mt-4'
                          }
                        >
                          <div className={'col-span-full sm:col-span-1'}>
                            <GanymedeInput
                              type={'tel'}
                              autoFocus
                              errors={errors}
                              maxLength={4}
                              label={'CVC'}
                              className={`selected-card-cvv-input ${
                                errors?.paymentMethod?.cvc
                                  ? 'invalid-input'
                                  : ''
                              }`}
                              {...register('paymentMethod.cvc', {
                                required: {
                                  value: true,
                                  message: 'CVC is required',
                                },
                                minLength: {
                                  value: 3,
                                  message: 'CVC is not valid',
                                },
                                min: 3,
                              })}
                            />
                          </div>
                          <div className={'col-span-full sm:col-span-3'}>
                            <img
                              className={'cvv-tip-image'}
                              alt={'cvv'}
                              src={Cvv}
                            />
                            Last 3 digits on the back of your card
                          </div>
                        </div>
                      )}
                    </label>
                  );
                })}
            </div>
            {paymentMethods?.length > DEFAULT_CARD_DISPLAY_LENGTH && (
              <>
                {!showAllCards && (
                  <button
                    onClick={() => setShowAllCards(true)}
                    className={'expand-payment-methods-btn'}
                  >
                    show all
                  </button>
                )}
                {showAllCards && (
                  <div className={'payment-methods'}>
                    {paymentMethods
                      ?.slice(DEFAULT_CARD_DISPLAY_LENGTH)
                      ?.map((card, index) => {
                        return (
                          <label
                            className={`payment-method ${
                              paymentMethod?.external_id === card.external_id
                                ? 'selected-payment-method'
                                : ''
                            }`}
                            key={index + DEFAULT_CARD_DISPLAY_LENGTH}
                          >
                            <div>
                              <div className={'grid-span-3'}>
                                <input
                                  name='paymentMethod'
                                  type='radio'
                                  {...register('paymentMethodRadio', {
                                    value: index.toString(),
                                    shouldUnregister: true,
                                    required: {
                                      value: true,
                                      message: 'Select a card',
                                    },
                                  })}
                                  value={index.toString()}
                                  defaultChecked={
                                    index.toString() === paymentMethodRadio
                                  }
                                />
                              </div>
                              <CardDisplay card={card} />
                            </div>
                            {paymentMethod?.external_id ===
                              card.external_id && (
                              <div
                                className={
                                  'cvv-capture grid grid-cols-6 grid-cols-6 gap-2 sm:mt-4'
                                }
                              >
                                <div className={'col-span-full sm:col-span-1'}>
                                  <GanymedeInput
                                    type={'tel'}
                                    autoFocus
                                    errors={errors}
                                    maxLength={4}
                                    label={'CVC'}
                                    className={`selected-card-cvv-input ${
                                      errors?.paymentMethod?.cvc
                                        ? 'invalid-input'
                                        : ''
                                    }`}
                                    {...register('paymentMethod.cvc', {
                                      required: {
                                        value: true,
                                        message: 'CVC is required',
                                      },
                                      minLength: {
                                        value: 3,
                                        message: 'CVC is not valid',
                                      },
                                      min: 3,
                                    })}
                                  />
                                </div>
                                <div className={'col-span-full sm:col-span-3'}>
                                  <img
                                    className={'cvv-tip-image'}
                                    alt={'cvv'}
                                    src={Cvv}
                                  />
                                  Last 3 digits on the back of your card
                                </div>
                              </div>
                            )}
                          </label>
                        );
                      })}
                  </div>
                )}
              </>
            )}
            {errors?.paymentMethodRadio && (
              <div className={'form-validation-error'}>
                {errors?.paymentMethodRadio.message}
              </div>
            )}
          </>
        )}
        {/* add mode - Stripe Elements */}
        {mode === 'add' && (
          <>
            {setupIntentError && (
              <div className={'form-validation-error'}>{setupIntentError}</div>
            )}
            {!setupIntentSecret && !setupIntentError && (
              <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>
                Loading payment form...
              </div>
            )}
            {setupIntentSecret && (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret: setupIntentSecret,
                  appearance: {
                    theme: 'stripe',
                    variables: {
                      colorPrimary: '#f97316',
                      fontFamily: 'Roboto, sans-serif',
                    },
                  },
                }}
              >
                <StripeCardForm
                  paymentMethods={paymentMethods}
                  cancel={cancel}
                  wallets={wallets}
                />
              </Elements>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default GanymedePaymentCardDisplay;
